<!-- ═══════════════════════════════════════════════════════════════════════════
     NEW DESIGN — Financial Data Extractor API  (useFinancialExtractAPI = true)
     Primary: /fmp/income-statement & /fmp/balance-sheet by symbol
     ═══════════════════════════════════════════════════════════════════════════ -->
<ng-container *ngIf="useNewApi">
<section class="fmp-fin-card">

  <!-- ── HEADER ── -->
  <div class="fmp-fin-header">
    <div class="fmp-fin-header-left">
      <h2 class="fmp-fin-title">Financial Statements</h2>
      <div class="fmp-fin-badges">
        <!-- <span *ngIf="fmpSymbol" class="fmp-fin-badge fmp-fin-badge-sym">{{ fmpSymbol }}</span> -->
        <!-- <span class="fmp-fin-badge fmp-fin-badge-src">FMP</span> -->
      </div>
    </div>
    <button class="fmp-fin-close-btn" aria-label="Close" type="button" data-dismiss="modal" (click)="closeModal()">
      <span aria-hidden="true">×</span>
    </button>
  </div>

  <!-- ── PRIMARY TAB NAV ── -->
  <div class="fmp-fin-tabnav">
    <button class="fmp-fin-tab" [class.active]="fmpSymActiveTab === 'income'" (click)="fmpSymActiveTab = 'income'">
      <span class="fmp-fin-tab-icon fmp-fin-icon-green"><i class="fa fa-line-chart"></i></span>
      Income Statement
    </button>
    <button class="fmp-fin-tab" [class.active]="fmpSymActiveTab === 'bs'" (click)="fmpSymActiveTab = 'bs'">
      <span class="fmp-fin-tab-icon fmp-fin-icon-blue"><i class="fa fa-balance-scale"></i></span>
      Balance Sheet
    </button>
    <ng-container *ngIf="isAdmin">
      <button class="fmp-fin-tab fmp-fin-tab-secondary" [class.active]="fmpSymActiveTab === 'pnl'" (click)="fmpSymActiveTab = 'pnl'">
        <span class="fmp-fin-tab-icon fmp-fin-icon-red"><i class="fa fa-file-pdf-o"></i></span>
        Upload P&amp;L PDF
      </button>
      <button class="fmp-fin-tab fmp-fin-tab-secondary" [class.active]="fmpSymActiveTab === 'bspdf'" (click)="fmpSymActiveTab = 'bspdf'">
        <span class="fmp-fin-tab-icon fmp-fin-icon-red"><i class="fa fa-file-pdf-o"></i></span>
        Upload BS PDF
      </button>
    </ng-container>
  </div>

  <!-- ── TAB BODY ── -->
  <div class="fmp-fin-body">

    <!-- ══ INCOME STATEMENT ══════════════════════════════════════════════ -->
    <div *ngIf="fmpSymActiveTab === 'income'" class="fmp-fin-tab-panel">

      <!-- toolbar: period pills + refresh -->
      <div class="fmp-fin-toolbar">
        <div class="fmp-fin-pills">
          <button [class.active]="fmpSymIncomePeriod === 'annual'" (click)="onFmpSymIncomePeriodChange('annual')">Annual</button>
          <button [class.active]="fmpSymIncomePeriod === 'quarterly'" (click)="onFmpSymIncomePeriodChange('quarterly')">Quarterly</button>
        </div>
        <button class="fmp-fin-refresh" (click)="loadFmpIncomeStatement(fmpSymbol)">
          <i class="fa fa-refresh"></i>&nbsp;Refresh
        </button>
      </div>

      <!-- no symbol -->
      <div *ngIf="!fmpSymbol" class="fmp-fin-state fmp-fin-state-warn">
        <i class="fa fa-info-circle fmp-fin-state-icon"></i>
        <p>No stock symbol is configured for this company.</p>
      </div>

      <!-- loading -->
      <div *ngIf="fmpSymbol && fmpSymIsLoadingIncome" class="fmp-fin-state">
        <i class="fa fa-circle-o-notch fa-spin fmp-fin-state-icon"></i>
        <p>Loading income statement for <strong>{{ fmpSymbol }}</strong>…</p>
      </div>

      <!-- error -->
      <div *ngIf="fmpSymbol && !fmpSymIsLoadingIncome && fmpSymIncomeError" class="fmp-fin-state fmp-fin-state-error">
        <i class="fa fa-exclamation-triangle fmp-fin-state-icon"></i>
        <p>{{ fmpSymIncomeError }}</p>
        <button class="btn btn-sm btn-outline-danger mt-2" (click)="loadFmpIncomeStatement(fmpSymbol)">
          <i class="fa fa-refresh"></i>&nbsp;Retry
        </button>
      </div>

      <!-- empty -->
      <div *ngIf="fmpSymbol && !fmpSymIsLoadingIncome && !fmpSymIncomeError && fmpIncomeDisplayRows.length === 0" class="fmp-fin-state">
        <i class="fa fa-inbox fmp-fin-state-icon"></i>
        <p>No {{ fmpSymIncomePeriod }} income statement data available for <strong>{{ fmpSymbol }}</strong>.</p>
      </div>

      <!-- data table -->
      <ng-container *ngIf="fmpSymbol && !fmpSymIsLoadingIncome && !fmpSymIncomeError && fmpIncomeDisplayRows.length > 0">
        <p class="fmp-fin-section-label">
          Income Statement — {{ fmpSymIncomePeriod === 'annual' ? 'Annual' : 'Quarterly' }}
          <span class="fmp-fin-label-sym">{{ fmpSymbol }}</span>
        </p>
        <div class="table-responsive fmp-fin-tbl-wrap">
          <table class="fmp-fin-tbl">
            <thead>
              <tr>
                <th class="fmp-fin-th-line">Line Item</th>
                <th *ngFor="let row of fmpIncomeDisplayRows" class="fmp-fin-th-date">{{ row.date }}</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let key of fmpIncomeDisplayKeys" [class.fmp-fin-row-hl]="isFmpHighlightRow(key)">
                <td class="fmp-fin-td-line">{{ formatFmpFieldName(key) }}</td>
                <td *ngFor="let row of fmpIncomeDisplayRows" class="fmp-fin-td-val">
                  {{ formatFmpFinancialValue(row[key], key) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </ng-container>
    </div>

    <!-- ══ BALANCE SHEET ══════════════════════════════════════════════════ -->
    <div *ngIf="fmpSymActiveTab === 'bs'" class="fmp-fin-tab-panel">

      <div class="fmp-fin-toolbar">
        <div class="fmp-fin-pills">
          <button [class.active]="fmpSymBsPeriod === 'annual'" (click)="onFmpSymBsPeriodChange('annual')">Annual</button>
          <button [class.active]="fmpSymBsPeriod === 'quarterly'" (click)="onFmpSymBsPeriodChange('quarterly')">Quarterly</button>
        </div>
        <button class="fmp-fin-refresh" (click)="loadFmpBalanceSheetFmpBySymbol(fmpSymbol)">
          <i class="fa fa-refresh"></i>&nbsp;Refresh
        </button>
      </div>

      <div *ngIf="!fmpSymbol" class="fmp-fin-state fmp-fin-state-warn">
        <i class="fa fa-info-circle fmp-fin-state-icon"></i>
        <p>No stock symbol is configured for this company.</p>
      </div>

      <div *ngIf="fmpSymbol && fmpSymIsLoadingBs" class="fmp-fin-state">
        <i class="fa fa-circle-o-notch fa-spin fmp-fin-state-icon"></i>
        <p>Loading balance sheet for <strong>{{ fmpSymbol }}</strong>…</p>
      </div>

      <div *ngIf="fmpSymbol && !fmpSymIsLoadingBs && fmpSymBsError" class="fmp-fin-state fmp-fin-state-error">
        <i class="fa fa-exclamation-triangle fmp-fin-state-icon"></i>
        <p>{{ fmpSymBsError }}</p>
        <button class="btn btn-sm btn-outline-danger mt-2" (click)="loadFmpBalanceSheetFmpBySymbol(fmpSymbol)">
          <i class="fa fa-refresh"></i>&nbsp;Retry
        </button>
      </div>

      <div *ngIf="fmpSymbol && !fmpSymIsLoadingBs && !fmpSymBsError && fmpBsDisplayRows.length === 0" class="fmp-fin-state">
        <i class="fa fa-inbox fmp-fin-state-icon"></i>
        <p>No {{ fmpSymBsPeriod }} balance sheet data available for <strong>{{ fmpSymbol }}</strong>.</p>
      </div>

      <ng-container *ngIf="fmpSymbol && !fmpSymIsLoadingBs && !fmpSymBsError && fmpBsDisplayRows.length > 0">
        <p class="fmp-fin-section-label">
          Balance Sheet — {{ fmpSymBsPeriod === 'annual' ? 'Annual' : 'Quarterly' }}
          <span class="fmp-fin-label-sym">{{ fmpSymbol }}</span>
        </p>
        <div class="table-responsive fmp-fin-tbl-wrap">
          <table class="fmp-fin-tbl">
            <thead>
              <tr>
                <th class="fmp-fin-th-line">Line Item</th>
                <th *ngFor="let row of fmpBsDisplayRows" class="fmp-fin-th-date">{{ row.date }}</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let key of fmpBsDisplayKeys" [class.fmp-fin-row-hl]="isFmpHighlightRow(key)">
                <td class="fmp-fin-td-line">{{ formatFmpFieldName(key) }}</td>
                <td *ngFor="let row of fmpBsDisplayRows" class="fmp-fin-td-val">
                  {{ formatFmpFinancialValue(row[key], key) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </ng-container>
    </div>

    <!-- ══ P&L PDF UPLOAD (Admin only) ═════════════════════════════════ -->
    <div *ngIf="fmpSymActiveTab === 'pnl' && isAdmin" class="fmp-fin-tab-panel">

      <div class="fmp-fin-pills fmp-fin-pills-mb">
        <button [class.active]="fmpActivePnlTab === 'quarterly'" (click)="fmpActivePnlTab = 'quarterly'">Quarterly</button>
        <button [class.active]="fmpActivePnlTab === 'yearly'" (click)="fmpActivePnlTab = 'yearly'">Annual</button>
      </div>

      <div *ngIf="!fmpPnlPdfId" class="fmp-fin-upload-zone">
        <i class="fa fa-file-pdf-o fa-3x fmp-fin-upload-icon"></i>
        <p class="fmp-fin-upload-hint">Upload a P&amp;L PDF to extract financial data</p>
        <label class="fmp-fin-upload-btn">
          <i class="fa fa-upload"></i>&nbsp;Choose PDF
          <input type="file" accept="application/pdf" style="display:none" (change)="onFmpPnlPdfChange($event)">
        </label>
        <div *ngIf="fmpIsUploadingPnl" class="fmp-fin-uploading mt-2">
          <i class="fa fa-spinner fa-spin"></i>&nbsp;Uploading &amp; extracting…
        </div>
        <div *ngIf="fmpPnlUploadError" class="fmp-fin-upload-error mt-2">
          <i class="fa fa-exclamation-circle"></i>&nbsp;{{ fmpPnlUploadError }}
        </div>
      </div>

      <div *ngIf="fmpPnlPdfId" class="fmp-fin-pdf-loaded">
        <span class="fmp-fin-pdf-tag"><i class="fa fa-file-pdf-o"></i>&nbsp;PDF loaded</span>
        <button class="fmp-fin-pdf-reset" (click)="fmpPnlPdfId = ''; fmpPnlQuarterlyData = null; fmpPnlYearlyData = null;">
          <i class="fa fa-times"></i>&nbsp;Upload different
        </button>
      </div>

      <div *ngIf="fmpPnlPdfId && fmpActivePnlTab === 'quarterly'">
        <div *ngIf="fmpIsLoadingPnlQuarterly" class="fmp-fin-state">
          <i class="fa fa-spinner fa-spin fmp-fin-state-icon"></i><p>Loading quarterly P&amp;L…</p>
        </div>
        <ng-container *ngIf="!fmpIsLoadingPnlQuarterly && fmpPnlQuarterlyData">
          <p class="fmp-fin-section-label">Profit &amp; Loss — Quarterly</p>
          <div class="table-responsive fmp-fin-tbl-wrap">
            <table class="fmp-fin-tbl">
              <thead><tr>
                <th class="fmp-fin-th-line">Line Item</th>
                <th *ngFor="let col of fmpPnlQuarterlyColumns" class="fmp-fin-th-date">{{ col }}</th>
              </tr></thead>
              <tbody>
                <tr *ngFor="let row of fmpPnlQuarterlyRows">
                  <td class="fmp-fin-td-line">{{ row.label }}</td>
                  <td *ngFor="let col of fmpPnlQuarterlyColumns" class="fmp-fin-td-val">{{ row[col] }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </ng-container>
        <div *ngIf="!fmpIsLoadingPnlQuarterly && !fmpPnlQuarterlyData" class="fmp-fin-state">
          <i class="fa fa-inbox fmp-fin-state-icon"></i><p>No quarterly P&amp;L data available.</p>
        </div>
      </div>

      <div *ngIf="fmpPnlPdfId && fmpActivePnlTab === 'yearly'">
        <div *ngIf="fmpIsLoadingPnlYearly" class="fmp-fin-state">
          <i class="fa fa-spinner fa-spin fmp-fin-state-icon"></i><p>Loading annual P&amp;L…</p>
        </div>
        <ng-container *ngIf="!fmpIsLoadingPnlYearly && fmpPnlYearlyData">
          <p class="fmp-fin-section-label">Profit &amp; Loss — Annual</p>
          <div class="table-responsive fmp-fin-tbl-wrap">
            <table class="fmp-fin-tbl">
              <thead><tr>
                <th class="fmp-fin-th-line">Line Item</th>
                <th *ngFor="let col of fmpPnlYearlyColumns" class="fmp-fin-th-date">{{ col }}</th>
              </tr></thead>
              <tbody>
                <tr *ngFor="let row of fmpPnlYearlyRows">
                  <td class="fmp-fin-td-line">{{ row.label }}</td>
                  <td *ngFor="let col of fmpPnlYearlyColumns" class="fmp-fin-td-val">{{ row[col] }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </ng-container>
        <div *ngIf="!fmpIsLoadingPnlYearly && !fmpPnlYearlyData" class="fmp-fin-state">
          <i class="fa fa-inbox fmp-fin-state-icon"></i><p>No annual P&amp;L data available.</p>
        </div>
      </div>
    </div>

    <!-- ══ BALANCE SHEET PDF UPLOAD (Admin only) ════════════════════════ -->
    <div *ngIf="fmpSymActiveTab === 'bspdf' && isAdmin" class="fmp-fin-tab-panel">

      <div *ngIf="!fmpBsPdfId" class="fmp-fin-upload-zone">
        <i class="fa fa-file-pdf-o fa-3x fmp-fin-upload-icon"></i>
        <p class="fmp-fin-upload-hint">Upload a Balance Sheet PDF to extract data</p>
        <label class="fmp-fin-upload-btn">
          <i class="fa fa-upload"></i>&nbsp;Choose PDF
          <input type="file" accept="application/pdf" style="display:none" (change)="onFmpBsPdfChange($event)">
        </label>
        <div *ngIf="fmpIsUploadingBs" class="fmp-fin-uploading mt-2">
          <i class="fa fa-spinner fa-spin"></i>&nbsp;Uploading &amp; extracting…
        </div>
        <div *ngIf="fmpBsUploadError" class="fmp-fin-upload-error mt-2">
          <i class="fa fa-exclamation-circle"></i>&nbsp;{{ fmpBsUploadError }}
        </div>
      </div>

      <div *ngIf="fmpBsPdfId" class="fmp-fin-pdf-loaded">
        <span class="fmp-fin-pdf-tag"><i class="fa fa-file-pdf-o"></i>&nbsp;PDF loaded</span>
        <button class="fmp-fin-pdf-reset" (click)="fmpBsPdfId = ''; fmpBsData = null;">
          <i class="fa fa-times"></i>&nbsp;Upload different
        </button>
      </div>

      <div *ngIf="fmpBsPdfId">
        <div *ngIf="fmpIsLoadingBs" class="fmp-fin-state">
          <i class="fa fa-spinner fa-spin fmp-fin-state-icon"></i><p>Loading balance sheet…</p>
        </div>
        <ng-container *ngIf="!fmpIsLoadingBs && fmpBsData">
          <p class="fmp-fin-section-label">Balance Sheet (PDF)</p>
          <div class="table-responsive fmp-fin-tbl-wrap">
            <table class="fmp-fin-tbl">
              <thead><tr>
                <th class="fmp-fin-th-line">Line Item</th>
                <th *ngFor="let col of fmpBsColumns" class="fmp-fin-th-date">{{ col }}</th>
              </tr></thead>
              <tbody>
                <tr *ngFor="let row of fmpBsRows">
                  <td class="fmp-fin-td-line">{{ row.label }}</td>
                  <td *ngFor="let col of fmpBsColumns" class="fmp-fin-td-val">{{ row[col] }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </ng-container>
        <div *ngIf="!fmpIsLoadingBs && !fmpBsData" class="fmp-fin-state">
          <i class="fa fa-inbox fmp-fin-state-icon"></i><p>No balance sheet data available.</p>
        </div>
      </div>
    </div>

  </div><!-- /fmp-fin-body -->

  <!-- ── FOOTER ── -->
  <div class="fmp-fin-footer">
    <!-- <span class="fmp-fin-footer-src">Source: Financial Market Platform (FMP)</span> -->
  </div>

</section>
</ng-container>

<!-- ═══════════════════════════════════════════════════════════════════════════
     LEGACY DESIGN — Robocs / clientUrl API  (useFinancialExtractAPI = false)
     ═══════════════════════════════════════════════════════════════════════════ -->
<ng-container *ngIf="!useNewApi">
<!-- START: Financials Component -->
<section class="card mt-4 financeResult">
    <button aria-label="Close" class="close" type="button" data-dismiss="modal" (click)="closeModal()">
        <span aria-hidden="true">×</span>
    </button>
    
    <!-- Main Navigation: Profit & Loss | Balance Sheet -->
    <div class="financial-nav-buttons">
        <button 
            type="button" 
            class="btn-financial-nav"
            [class.active]="activeMainTab === 'profitAndLoss'"
            (click)="onMainTabChange('profitAndLoss')">
            Profit & Loss
        </button>
        <button 
            type="button" 
            class="btn-financial-nav"
            [class.active]="activeMainTab === 'balanceSheet'"
            (click)="onMainTabChange('balanceSheet')">
            Balance Sheet
        </button>
    </div>
    
    <div class="financial-content-wrapper">
        <!-- Profit & Loss Content -->
        <div class="financial-section" *ngIf="activeMainTab === 'profitAndLoss'">
            <div class="financial-sub-nav-buttons">
                <button 
                    type="button" 
                    class="btn-financial-sub-nav"
                    [class.active]="activePLTab === 'quarterly'"
                    (click)="onPLTabChange('quarterly')">
                    Quarterly Trend
                </button>
                <button 
                    type="button" 
                    class="btn-financial-sub-nav"
                    [class.active]="activePLTab === 'annual'"
                    (click)="onPLTabChange('annual')">
                    Annual Trend
                </button>
            </div>
            
            <div class="financial-type-nav-buttons">
                <button 
                    type="button" 
                    class="btn-financial-type-nav"
                    [class.active]="activeTypeTab === 'standalone'"
                    (click)="onTypeTabChange('standalone')">
                    Standalone
                </button>
                <button 
                    type="button" 
                    class="btn-financial-type-nav"
                    [class.active]="activeTypeTab === 'consolidated'"
                    (click)="onTypeTabChange('consolidated')">
                    Consolidated
                </button>
            </div>
            
            <div class="title-2">
                PROFIT & LOSS ({{ activePLTab === 'quarterly' ? 'QUARTERLY' : 'ANNUAL' }})
            </div>
            <!-- <div class="title-sub">
                Toggle view and type to explore Profit & Loss quickly.
            </div> -->
            
            <div class="content-area">
                <div *ngIf="activePLTab === 'quarterly'">
                    <!-- Standalone Quarterly P&L -->
                    <div *ngIf="activeTypeTab === 'standalone'">
                        <div *ngIf="isLoadingPLQuarterly" class="text-center my-3" style="padding: 40px;">
                            <i class="fa fa-spinner fa-spin fa-2x"></i>
                            <div style="margin-top: 10px;">Loading quarterly profit & loss data...</div>
                        </div>
                        <div *ngIf="!isLoadingPLQuarterly && (!arrangedDataStandalone || arrangedDataStandalone.length === 0)" class="text-center my-3" style="padding: 40px;">
                            <p>No quarterly profit & loss data available.</p>
                        </div>
                        
                        <!-- Charts Section - Show First -->
                        <div *ngIf="!isLoadingPLQuarterly && arrangedDataStandalone && arrangedDataStandalone.length > 0 && headerDatesStandalone && headerDatesStandalone.length > 0" class="mt-3 mb-4">
                            <h5 class="mb-3">Graphical Comparison</h5>
                            <div class="row">
                                <div class="col-md-12 mb-4">
                                    <div class="card">
                                        <div class="card-body">
                                            <h6 class="card-title text-center">Quarterly Profit & Loss - All Metrics</h6>
                                            <div style="position: relative; height: 500px;">
                                                <canvas id="quarterlyPLAllFieldsChartStandalone"></canvas>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div class="card-block watermark" *ngIf="!isLoadingPLQuarterly && arrangedDataStandalone && arrangedDataStandalone.length > 0">
                            <div class="container" style="padding:0 20px 50px;">
                                <div class="table-responsive">
                                    <table class="table table-hover table-bordered table-striped dataTable" width="100%">
                                        <thead>
                                            <tr>
                                                <th>For The Period Ended</th>
                                                <th *ngFor="let h of headerDatesStandalone">{{ h }}</th>
                                              </tr>
                                        </thead>
                                        <tbody>
                                            <tr *ngFor="let row of arrangedDataStandalone">
                                                <td>{{ row.label }}</td>
                                                <td *ngFor="let h of headerDatesStandalone" class="text-end">
                                                  {{ displayValue(row[h], row.label || '') }}
                                                </td>
                                              </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Consolidated Quarterly P&L -->
                    <div *ngIf="activeTypeTab === 'consolidated'">
                        <div *ngIf="isLoadingPLQuarterly" class="text-center my-3" style="padding: 40px;">
                            <i class="fa fa-spinner fa-spin fa-2x"></i>
                            <div style="margin-top: 10px;">Loading quarterly profit & loss data...</div>
                        </div>
                        <div *ngIf="!isLoadingPLQuarterly && (!plQuarterlyDataConsolidated || plQuarterlyDataConsolidated.length === 0)" class="text-center my-3" style="padding: 40px;">
                            <p>No quarterly profit & loss data available.</p>
                        </div>
                        
                        <!-- Charts Section - Show First -->
                        <div *ngIf="!isLoadingPLQuarterly && arrangedDataConsolidated && arrangedDataConsolidated.length > 0 && dateHeadersConsolidated && dateHeadersConsolidated.length > 0" class="mt-3 mb-4">
                            <h5 class="mb-3">Graphical Comparison</h5>
                            <div class="row">
                                <div class="col-md-12 mb-4">
                                    <div class="card">
                                        <div class="card-body">
                                            <h6 class="card-title text-center">Quarterly Profit & Loss - All Metrics</h6>
                                            <div style="position: relative; height: 500px;">
                                                <canvas id="quarterlyPLAllFieldsChartConsolidated"></canvas>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div class="card-block watermark" *ngIf="!isLoadingPLQuarterly && plQuarterlyDataConsolidated && plQuarterlyDataConsolidated.length > 0">
                            <div class="container" style="padding:0 20px 50px;">
                                <div class="table-responsive">
                                    <table class="table table-hover table-bordered table-striped dataTable" width="100%">
                                        <thead>
                                            <tr>
                                                <th>For The Period Ended</th>
                                                <th *ngFor="let h of dateHeadersConsolidated">{{ h }}</th>
                                              </tr>
                                        </thead>
                                        <tbody>
                                            <tr *ngFor="let row of arrangedDataConsolidated">
                                                <td>{{ row.label }}</td>
                                                <td *ngFor="let h of dateHeadersConsolidated" class="text-end">
                                                  {{ displayValue(row[h], row.label || '') }}
                                                </td>
                                              </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div *ngIf="activePLTab === 'annual'">
                    <!-- Standalone Annual P&L -->
                    <div *ngIf="activeTypeTab === 'standalone'">
                        <div *ngIf="isLoadingPLAnnual" class="text-center my-3" style="padding: 40px;">
                            <i class="fa fa-spinner fa-spin fa-2x"></i>
                            <div style="margin-top: 10px;">Loading annual profit & loss data...</div>
                        </div>
                        <div *ngIf="!isLoadingPLAnnual && (!plAnnualDataStandalone || plAnnualDataStandalone.length === 0)" class="text-center my-3" style="padding: 40px;">
                            <p>No annual profit & loss data available.</p>
                        </div>
                        
                        <!-- Charts Section - Show First -->
                        <div *ngIf="!isLoadingPLAnnual && plAnnualDataStandalone && plAnnualDataStandalone.length > 0 && plAnnualStandaloneHeaders && plAnnualStandaloneHeaders.length > 0" class="mt-3 mb-4">
                            <h5 class="mb-3">Graphical Comparison</h5>
                            <div class="row">
                                <div class="col-md-12 mb-4">
                                    <div class="card">
                                        <div class="card-body">
                                            <h6 class="card-title text-center">Annual Profit & Loss - All Metrics</h6>
                                            <div style="position: relative; height: 500px;">
                                                <canvas id="annualPLAllFieldsChartStandalone"></canvas>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div class="card-block watermark" *ngIf="!isLoadingPLAnnual && plAnnualDataStandalone && plAnnualDataStandalone.length > 0">
                            <div class="container" style="padding:0 20px 50px;">
                                <div class="table-responsive">
                                    <table class="table table-hover table-bordered table-striped dataTable" width="100%">
                                        <thead>
                                            <tr>
                                                <th style="text-align: left;">For The Year Ended</th>
                                                <th *ngFor="let header of plAnnualStandaloneHeaders" style="text-align: right;">
                                                    {{ formatYearHeader(header) || '-' }}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <ng-container *ngFor="let row of plAnnualDataStandalone; let i = index">
                                                <!-- Insert section heading if needed -->
                                                <tr *ngIf="shouldInsertSectionHeading(row.label || row[0] || '', plAnnualDataStandalone[i-1]?.label || plAnnualDataStandalone[i-1]?.[0] || '', i)">
                                                    <td class="bs-main-heading" style="text-align: left; padding-left: 10px;">
                                                        {{ getSectionHeading(row.label || row[0] || '', i) }}
                                                    </td>
                                                    <td *ngFor="let header of plAnnualStandaloneHeaders" style="text-align: right;"></td>
                                                </tr>
                                                <!-- Data row -->
                                                <tr>
                                                    <td [ngClass]="getRowClass(row.label || row[0] || '')" [style.text-align]="'left'" [style.padding-left]="getRowPadding(row.label || row[0] || '')">
                                                        {{ row.label || row[0] || '-' }}
                                                    </td>
                                                    <td *ngFor="let header of plAnnualStandaloneHeaders" style="text-align: right;">
                                                        {{ displayValue(row[header], row.label || row[0] || '') }}
                                                    </td>
                                                </tr>
                                            </ng-container>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Consolidated Annual P&L -->
                    <div *ngIf="activeTypeTab === 'consolidated'">
                        <div *ngIf="isLoadingPLAnnual" class="text-center my-3" style="padding: 40px;">
                            <i class="fa fa-spinner fa-spin fa-2x"></i>
                            <div style="margin-top: 10px;">Loading annual profit & loss data...</div>
                        </div>
                        <div *ngIf="!isLoadingPLAnnual && (!plAnnualDataConsolidated || plAnnualDataConsolidated.length === 0)" class="text-center my-3" style="padding: 40px;">
                            <p>No annual profit & loss data available.</p>
                        </div>
                        
                        <!-- Charts Section - Show First -->
                        <div *ngIf="!isLoadingPLAnnual && plAnnualDataConsolidated && plAnnualDataConsolidated.length > 0 && plAnnualConsolidatedHeaders && plAnnualConsolidatedHeaders.length > 0" class="mt-3 mb-4">
                            <h5 class="mb-3">Graphical Comparison</h5>
                            <div class="row">
                                <div class="col-md-12 mb-4">
                                    <div class="card">
                                        <div class="card-body">
                                            <h6 class="card-title text-center">Annual Profit & Loss - All Metrics</h6>
                                            <div style="position: relative; height: 500px;">
                                                <canvas id="annualPLAllFieldsChartConsolidated"></canvas>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div class="card-block watermark" *ngIf="!isLoadingPLAnnual && plAnnualDataConsolidated && plAnnualDataConsolidated.length > 0">
                            <div class="container" style="padding:0 20px 50px;">
                                <div class="table-responsive">
                                    <table class="table table-hover table-bordered table-striped dataTable" width="100%">
                                        <thead>
                                            <tr>
                                                <th style="text-align: left;">For The Year Ended</th>
                                                <th *ngFor="let header of plAnnualConsolidatedHeaders" style="text-align: right;">
                                                    {{ formatYearHeader(header) || '-' }}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <ng-container *ngFor="let row of plAnnualDataConsolidated; let i = index">
                                                <!-- Insert section heading if needed -->
                                                <tr *ngIf="shouldInsertSectionHeading(row.label || row[0] || '', plAnnualDataConsolidated[i-1]?.label || plAnnualDataConsolidated[i-1]?.[0] || '', i)">
                                                    <td class="bs-main-heading" style="text-align: left; padding-left: 10px;">
                                                        {{ getSectionHeading(row.label || row[0] || '', i) }}
                                                    </td>
                                                    <td *ngFor="let header of plAnnualConsolidatedHeaders" style="text-align: right;"></td>
                                                </tr>
                                                <!-- Data row -->
                                                <tr>
                                                    <td [ngClass]="getRowClass(row.label || row[0] || '')" [style.text-align]="'left'" [style.padding-left]="getRowPadding(row.label || row[0] || '')">
                                                        {{ row.label || row[0] || '-' }}
                                                    </td>
                                                    <td *ngFor="let header of plAnnualConsolidatedHeaders" style="text-align: right;">
                                                        {{ displayValue(row[header], row.label || row[0] || '') }}
                                                    </td>
                                                </tr>
                                            </ng-container>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Balance Sheet Content -->
        <div class="financial-section" *ngIf="activeMainTab === 'balanceSheet'">
            <div class="financial-type-nav-buttons">
                <button 
                    type="button" 
                    class="btn-financial-type-nav"
                    [class.active]="activeTypeTab === 'standalone'"
                    (click)="onTypeTabChange('standalone')">
                    Standalone
                </button>
                <button 
                    type="button" 
                    class="btn-financial-type-nav"
                    [class.active]="activeTypeTab === 'consolidated'"
                    (click)="onTypeTabChange('consolidated')">
                    Consolidated
                </button>
            </div>
            
            <div class="title-2">
                BALANCE SHEET ({{ activeTypeTab === 'standalone' ? 'STANDALONE' : 'CONSOLIDATED' }})
            </div>
            <!-- <div class="title-sub">
                Switch type to review the corresponding Balance Sheet.
            </div> -->
            
            <div class="content-area">
                <!-- Standalone Balance Sheet -->
                <div *ngIf="activeTypeTab === 'standalone'">
                    <div *ngIf="isLoadingBSStandalone" class="text-center my-3" style="padding: 40px;">
                        <i class="fa fa-spinner fa-spin fa-2x"></i>
                        <div style="margin-top: 10px;">Loading standalone balance sheet data...</div>
                    </div>
                    <div *ngIf="!isLoadingBSStandalone && (!balanceSheetStandaloneData || balanceSheetStandaloneData.length === 0)" class="text-center my-3" style="padding: 40px;">
                        <p>No standalone balance sheet data available.</p>
                        <p style="font-size: 12px; color: #666;">Data length: {{ balanceSheetStandaloneData?.length || 0 }}</p>
                    </div>
                    
                    <!-- Charts Section - Show First -->
                    <div *ngIf="!isLoadingBSStandalone && balanceSheetStandaloneData && balanceSheetStandaloneData.length > 0 && balanceSheetStandaloneHeaders && balanceSheetStandaloneHeaders.length > 0" class="mt-3 mb-4">
                        <h5 class="mb-3">Graphical Comparison</h5>
                        <div class="row">
                            <div class="col-md-12 mb-4">
                                <div class="card">
                                    <div class="card-body">
                                        <h6 class="card-title text-center">Balance Sheet - All Metrics</h6>
                                        <div style="position: relative; height: 500px;">
                                            <canvas id="balanceSheetAllFieldsChartStandalone"></canvas>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="card-block watermark" *ngIf="!isLoadingBSStandalone && balanceSheetStandaloneData && balanceSheetStandaloneData.length > 0">
                        <div class="container" style="padding:0 20px 50px;">
                            <div class="table-responsive">
                                <table class="table table-hover table-bordered table-striped dataTable" width="100%">
                                    <thead>
                                        <tr>
                                            <th style="text-align: left;">For The Year Ended</th>
                                            <th *ngFor="let header of balanceSheetStandaloneHeaders" style="text-align: right;">
                                                {{ formatYearHeader(header) || '-' }}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <ng-container *ngFor="let row of balanceSheetStandaloneData; let i = index">
                                            <!-- Insert section heading (ASSETS or EQUITIES AND LIABILITIES) before appropriate rows -->
                                            <tr *ngIf="shouldInsertSectionHeading(row.label || row[0] || '', balanceSheetStandaloneData[i-1]?.label || balanceSheetStandaloneData[i-1]?.[0] || '', i)">
                                                <td class="bs-main-heading" style="text-align: left; padding-left: 10px;">
                                                    {{ getSectionHeading(row.label || row[0] || '', i) }}
                                                </td>
                                                <td *ngFor="let header of balanceSheetStandaloneHeaders" style="text-align: right;"></td>
                                            </tr>
                                            <!-- Data row -->
                                            <tr>
                                                <td [ngClass]="getRowClass(row.label || row[0] || '')" [style.text-align]="'left'" [style.padding-left]="getRowPadding(row.label || row[0] || '')">
                                                    {{ row.label || row[0] || '-' }}
                                                </td>
                                                <td *ngFor="let header of balanceSheetStandaloneHeaders" style="text-align: right;">
                                                    {{ displayValue(row[header], row.label || row[0] || '') }}
                                                </td>
                                            </tr>
                                        </ng-container>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- Consolidated Balance Sheet -->
                <div *ngIf="activeTypeTab === 'consolidated'">
                    <div *ngIf="isLoadingBSConsolidated" class="text-center my-3" style="padding: 40px;">
                        <i class="fa fa-spinner fa-spin fa-2x"></i>
                        <div style="margin-top: 10px;">Loading consolidated balance sheet data...</div>
                    </div>
                    <div *ngIf="!isLoadingBSConsolidated && (!balanceSheetConsolidatedData || balanceSheetConsolidatedData.length === 0)" class="text-center my-3" style="padding: 40px;">
                        <p>No consolidated balance sheet data available.</p>
                    </div>
                    
                    <!-- Charts Section - Show First -->
                    <div *ngIf="!isLoadingBSConsolidated && balanceSheetConsolidatedData && balanceSheetConsolidatedData.length > 0 && balanceSheetConsolidatedHeaders && balanceSheetConsolidatedHeaders.length > 0" class="mt-3 mb-4">
                        <h5 class="mb-3">Graphical Comparison</h5>
                        <div class="row">
                            <div class="col-md-12 mb-4">
                                <div class="card">
                                    <div class="card-body">
                                        <h6 class="card-title text-center">Balance Sheet - All Metrics</h6>
                                        <div style="position: relative; height: 500px;">
                                            <canvas id="balanceSheetAllFieldsChartConsolidated"></canvas>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="card-block watermark" *ngIf="!isLoadingBSConsolidated && balanceSheetConsolidatedData && balanceSheetConsolidatedData.length > 0">
                        <div class="container" style="padding:0 20px 50px;">
                            <div class="table-responsive">
                                <table class="table table-hover table-bordered table-striped dataTable" width="100%">
                                    <thead>
                                        <tr>
                                            <th style="text-align: left;">For The Year Ended</th>
                                            <th *ngFor="let header of balanceSheetConsolidatedHeaders" style="text-align: right;">
                                                {{ formatYearHeader(header) || '-' }}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <ng-container *ngFor="let row of balanceSheetConsolidatedData; let i = index">
                                            <!-- Insert section heading (ASSETS or EQUITIES AND LIABILITIES) before appropriate rows -->
                                            <tr *ngIf="shouldInsertSectionHeading(row.label || row[0] || '', balanceSheetConsolidatedData[i-1]?.label || balanceSheetConsolidatedData[i-1]?.[0] || '', i)">
                                                <td class="bs-main-heading" style="text-align: left; padding-left: 10px;">
                                                    {{ getSectionHeading(row.label || row[0] || '', i) }}
                                                </td>
                                                <td *ngFor="let header of balanceSheetConsolidatedHeaders" style="text-align: right;"></td>
                                            </tr>
                                            <!-- Data row -->
                                            <tr>
                                                <td [ngClass]="getRowClass(row.label || row[0] || '')" [style.text-align]="'left'" [style.padding-left]="getRowPadding(row.label || row[0] || '')">
                                                    {{ row.label || row[0] || '-' }}
                                                </td>
                                                <td *ngFor="let header of balanceSheetConsolidatedHeaders" style="text-align: right;">
                                                    {{ displayValue(row[header], row.label || row[0] || '') }}
                                                </td>
                                            </tr>
                                        </ng-container>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</section>
</ng-container><!-- end legacy design -->
